import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, IMAGE_PATH } from "../../services/api";
import "./FeaturedCollections.css";
import { useNavigate } from "react-router-dom";


// Motion variants
const slideVariant = {
    enter: { opacity: 0, y: 60 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -60 },
};
const normalizeImages = (images) => {
    if (!images) return [];

    if (Array.isArray(images)) return images;

    if (typeof images === "string") {
        try {
            const parsed = JSON.parse(images);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            if (images.includes(",")) {
                return images.split(",").map(s => s.trim()).filter(Boolean);
            }
            return [images];
        }
    }

    return [];
};
export default function FeaturedCollections() {
    const [collections, setCollections] = useState({});
    const [activeKey, setActiveKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const ALLOWED_CATEGORIES = [
        "Rings",
        "Anklets",
        "Bracelets",
        "Earrings",
    ];
  

    // Normalize category values: treat null / "null" / "" as no category
    const normalizeCategory = (value) => {
        if (value === null || value === undefined) return null;
        if (typeof value === "string" && value.trim().toLowerCase() === "null") {
            return null;
        }
        if (typeof value === "string" && value.trim() === "") return null;
        return value;
    };
   
    /* ---------------- FETCH PRODUCTS + BUILD COLLECTIONS ---------------- */
    useEffect(() => {
        async function fetchData() {
            try {
                const res = await axios.get(API.getProducts);
                // const products = res.data || [];
                const raw = res?.data;

                const products = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data)
                        ? raw.data
                        : [];

                // Group products by normalized category
                const grouped = {};
                if (!Array.isArray(products)) {
                    console.warn("FeaturedCollections: products is not array", products);
                    return;
                }

                products.forEach((p) => {
                    let cat = normalizeCategory(p.mainCategory);

                    // fallback ONLY if mainCategory missing
                    if (!cat) {
                        cat = normalizeCategory(p.category);
                    }

                    if (!cat) return;

                    // ✅ allow ONLY specific categories
                    if (!ALLOWED_CATEGORIES.includes(cat)) return;

                    if (!grouped[cat]) {
                        grouped[cat] = [];
                    }

                    grouped[cat].push(p);
                });

                // Pick top 3 categories by product count
                const topCategories = Object.entries(grouped)
                    .slice(0, 4);


                const formatted = {};

                topCategories.forEach(([catName, productList]) => {
                    // Sort products by created/updated date (newest first)
                    const sortedProducts = [...productList].sort(
                        (a, b) => new Date(b.created_at) - new Date(a.created_at)
                    );

                    const heroProduct = sortedProducts[0];

                    // Parse images of hero product
                    let heroImage = "/fallback.avif";
                    let thumbs = [];

                    const imgs = normalizeImages(heroProduct?.images);

                    if (imgs.length > 0) {
                        heroImage = IMAGE_PATH + imgs[0];
                        thumbs = imgs.slice(1, 3).map((fname) => IMAGE_PATH + fname).filter(Boolean);
                    }
                    // If we still don't have at least 2 thumbs, we can try other products in category
                    if (thumbs.length < 2 && sortedProducts.length > 1) {
                        sortedProducts.slice(1).forEach((prod) => {
                            if (thumbs.length >= 2) return;
                            if (!prod.images) return;
                            const imgs2 = normalizeImages(prod.images);
                            if (imgs2[0]) thumbs.push(IMAGE_PATH + imgs2[0]);
                        });
                    }

                    formatted[catName] = {
                        key: catName,
                        label: catName,
                        heroImage,
                        thumbs,
                        description:
                            heroProduct?.description ||
                            heroProduct?.short_description ||
                            "",
                    };

                });

                setCollections(formatted);

                const keys = Object.keys(formatted);
                if (keys.length > 0) {
                    setActiveKey(keys[0]); // first category active by default
                }
            } catch (err) {
                console.error("Failed to load featured collections:", err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, []);

    // if (loading || !activeKey) {
    if (loading || !activeKey || !collections[activeKey]) {

        return (
            <section className="container" style={{ padding: "40px 5vw" }}>
                <p>Loading featured collections...</p>
            </section>
        );
    }

    const active = collections[activeKey];

    /* ---------------- INLINE STYLES (same as your original) ---------------- */
    const section = {
        width: "100%",
        padding: "40px 5vw",
        boxSizing: "border-box",
    };

    const inner = {
        display: "flex",
        alignItems: "center",
    };

    const left = {
        width: "40%",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
    };

    const right = {
        width: "60%",
        display: "flex",
        justifyContent: "flex-end",
        position: "relative",
        alignItems: "center",
    };

    const heroWrapper = {
        width: "100%",
        maxWidth: "620px",     // ⬅️ INCREASE (420 → 520)
        aspectRatio: "3 / 4",  // keep portrait ratio
        borderRadius: "16px",
        overflow: "hidden",
        border: "1px solid #ddd",
        boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
        position: "relative",
    };



    const heroImg = {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
    };

    const thumbContainer = {
        display: "flex",
        gap: "10px",
        marginTop: "18px",
    };

    const thumb = {
        width: "90px",
        height: "90px",
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #ddd",
    };

    return (
        <motion.section
            style={section}
            className="futured container"
            variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: "easeOut" },
                },
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.25 }}
        >
            <div style={inner} className="futured">
                {/* LEFT SIDE */}
                <div style={left} className="left">
                    <p
                        style={{
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            color: "#666",
                            fontWeight: "600",
                        }}
                    >
                        Featured Collections
                    </p>

                    {/* Category Buttons */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {Object.values(collections).map((item) => (
                            <button
                                key={item.key}
                                onClick={() => setActiveKey(item.key)}
                                style={{
                                    fontSize: "38px",
                                    background: "none",
                                    border: "none",
                                    textAlign: "left",
                                    fontWeight: activeKey === item.key ? 700 : 500,
                                    color: activeKey === item.key ? "#000" : "#b4b4b4",
                                    cursor: "pointer",
                                }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* TOP THUMBNAILS */}
                    <motion.div
                        style={thumbContainer}
                        key={`thumbs-top-${activeKey}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="motionfuture"
                    >
                        {active.thumbs.map((src, i) => (
                            <div style={thumb} key={i}>
                                <img
                                    src={src}
                                    alt=""
                                    onError={(e) => (e.currentTarget.src = "/fallback.avif")}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            </div>
                        ))}
                    </motion.div>

                    <div className="so">
                        <p style={{ fontSize: "12px", color: "#666" }}>
                            {active.description}
                        </p>

                        <button
                            type="button"
                            className="shopcollection"
                            onClick={() =>
                                navigate(
                                    `/products?category=${encodeURIComponent(active.label)}`
                                )
                            }
                            style={{
                                padding: "10px 24px",
                                borderRadius: "999px",
                                background: "#000",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                            }}
                        >
                            Shop Collection
                        </button>
                    </div>
                </div>

                {/* RIGHT SIDE IMAGE */}
                <div style={right} className="rightt">
                    <div style={heroWrapper} className="rightimage">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={active.heroImage}
                                src={active.heroImage}
                                style={heroImg}
                                variants={slideVariant}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.5, ease: "easeOut" }}
                                onError={(e) => (e.currentTarget.src = "/fallback.avif")}
                            />
                        </AnimatePresence>
                    </div>
                </div>

                {/* BOTTOM SECTION (MOBILE) */}
                <div className="bottom">
                    <div className="bo1">
                        <motion.div
                            style={thumbContainer}
                            key={`thumbs-bottom-${activeKey}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            {active.thumbs.map((src, i) => (
                                <div style={thumb} key={i}>
                                    <img
                                        src={src}
                                        alt=""
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    <div className="bo2">
                        <p style={{ fontSize: "12px", color: "#666" }}>
                            designed by avnii
                        </p>
                        <button
                            type="button"
                            className="shopcollection"
                            onClick={() =>
                                navigate(
                                    `/products?category=${encodeURIComponent(active.label)}`
                                )
                            }
                            style={{
                                padding: "10px 24px",
                                borderRadius: "999px",
                                background: "#000",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                            }}
                        >
                            Shop Collection
                        </button>
                    </div>
                </div>
            </div>
        </motion.section>
    );

}
